import { readBundledData } from "@faire/deprecated-retailer-visitor-shared/lib/bundledData";
import {
  useQuery,
  getQueryKey,
  getQueryFn,
} from "@faire/web-api--source/endpoints/www/api/v2/product/productToken/get-hooks";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { QueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { ProductDataContext } from "../context/ProductDataContext";

const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;

/**
 * Enhanced hook that checks multiple sources for server-populated product data
 * to prevent duplicate fetches during PDP loads and navigation.
 */
export const useLeanProductQuery = (
  productToken: string | undefined,
  options?: Parameters<typeof useQuery>[1]
) => {
  const productDataContext = useContext(ProductDataContext);

  return useQuery(productToken ?? "", {
    initialData: (): IProductPageResponse | undefined => {
      // If no product token, no initial data to provide
      if (!productToken) {
        return undefined;
      }

      // First, check bundled data (from server-side rendering)
      const prefetched = readBundledData("product_page", { keep: true });
      if (prefetched?.product?.token === productToken) {
        // Found matching bundled data
        return prefetched;
      }

      // Second, check PDP context for product data
      if (productDataContext.product?.token === productToken) {
        // Found matching data in PDP context - build response structure
        return IProductPageResponse.build({
          product: productDataContext.product,
          brand: productDataContext.brand,
        });
      }

      // No matching server data found - React Query will fetch from API
      return undefined;
    },
    enabled: !!productToken,
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnMount: false,
    ...options,
  });
};

export const prefetchLeanProduct = (
  queryClient: QueryClient,
  productToken: string
) => {
  void queryClient.prefetchQuery({
    queryKey: getQueryKey(productToken),
    queryFn: getQueryFn(productToken),
    staleTime: FIVE_MINUTES_IN_MS,
  });
};
